import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Chip } from '@jotpsych/ui';
import { Box, Button, Stack, Typography } from '@mui/material';

import type { EhrUpload } from '../../store/ehrApi';

export interface EhrUploadListProps {
  uploads: EhrUpload[];
  renderFiles?: (upload: EhrUpload) => ReactNode;
  dataImportUrl?: (upload: EhrUpload) => string;
}

export function EhrUploadList({ uploads, renderFiles, dataImportUrl }: EhrUploadListProps) {
  if (!uploads.length)
    return <Typography color="text.secondary">No EHR files uploaded yet.</Typography>;
  return (
    <Stack spacing={2}>
      {uploads.map(upload => (
        <Box
          key={upload.upload_id}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
        >
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">{upload.label || 'EHR export'}</Typography>
            {upload.status ? (
              <Chip
                size="small"
                variant="soft"
                color={upload.status === 'completed' ? 'success' : 'info'}
                label={upload.status === 'completed' ? 'Complete' : 'In progress'}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No files confirmed yet
              </Typography>
            )}
          </Stack>
          {renderFiles?.(upload)}
          <Typography variant="caption" color="text.secondary">
            {upload.file_count == null
              ? 'File count updating'
              : `${upload.file_count} file${upload.file_count === 1 ? '' : 's'}`}
            {upload.inventory_status === 'failed' && ' · File count could not refresh'}
          </Typography>
          <Stack direction="row" sx={{ mt: 1, gap: 1, flexWrap: 'wrap' }}>
            {dataImportUrl && (
              <Button component={RouterLink} to={dataImportUrl(upload)}>
                Open Data Import
              </Button>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
